import { Add } from "@dreamhub/designsystem/icons";
import LayoutTabs from "@/components/controls/LayoutTabs";
import Tabs, { tabsPropsType } from "@/components/controls/Tabs";
import WidgetCard from "@/components/controls/WidgetCard";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import TaskModal from "@/components/layouts/TasksDrawer/TaskModal";
import SearchButtonInput from "@/components/ui/search-button-input";
import { createModalStateSetter } from "@/entities/modals/helpers";
import toast from "react-hot-toast";
import {
  useDeleteTaskMutation,
  useFilteredTasks,
  useRefreshFilteredTasks,
  useRemoveTaskFromLists,
  useUndoDeleteTaskMutation
} from "@/entities/tasks/hooks";
import {
  DeleteTaskModalState,
  TaskModalState,
  TaskState,
  TaskTabs
} from "@/entities/tasks/types";
import { useUsersMe } from "@/entities/users/hooks";
import { EmptyFilters } from "@/services/api/types";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SuccessToast from "@/components/controls/Task/SuccessToast";
import CounterBadge from "@/components/ui/counter-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import TasksTable from "@/components/layouts/TasksFocus/TasksTable";
import EmptyTasksState from "@/components/layouts/TasksDrawer/EmptyTasksState";
import { IconButton } from "@/components/ui/icon-button";

const TRANSLATION_KEY = "focus.tasks";

const TasksFocus: React.FC = () => {
  const { t } = useTranslation();

  const tabsProps: tabsPropsType = {
    options: {
      [TaskState.OPEN]: t(`${TRANSLATION_KEY}.tabs.${TaskState.OPEN}`),
      [TaskState.COMPLETED]: t(`${TRANSLATION_KEY}.tabs.${TaskState.COMPLETED}`)
    },
    defaultValue: TaskState.OPEN
  };

  const layoutTabsProps: tabsPropsType = useMemo(
    () => ({
      options: {
        [TaskTabs.ALL_TASKS]: t(
          `${TRANSLATION_KEY}.layoutTabs.${TaskTabs.ALL_TASKS}`
        ),
        [TaskTabs.ASSIGNED_TO_ME]: t(
          `${TRANSLATION_KEY}.layoutTabs.${TaskTabs.ASSIGNED_TO_ME}`
        ),
        [TaskTabs.ASSIGNED_BY_ME]: t(
          `${TRANSLATION_KEY}.layoutTabs.${TaskTabs.ASSIGNED_BY_ME}`
        )
      },
      defaultValue: TaskTabs.ALL_TASKS
    }),
    [t]
  );

  const [selectedTab, setSelectedTab] = useState(tabsProps.defaultValue);
  const [selectedLayoutTab, setSelectedLayoutTab] = useState(
    layoutTabsProps.defaultValue
  );
  const [searchTerm, setSearchTerm] = useState("");

  const deleteTask = useDeleteTaskMutation();
  const undoDeleteTask = useUndoDeleteTaskMutation();

  const [taskModalState, setTaskModalState] = useState<TaskModalState>({
    isOpen: false,
    isEditMode: false,
    editTaskId: null
  });
  const [deleteTaskModalState, setDeleteTaskModalState] =
    useState<DeleteTaskModalState>({
      isOpen: false,
      task: {
        id: null,
        name: null
      }
    });

  const userData = useUsersMe().data;

  const baseFiltersForTotals = useMemo(
    () => ({
      ...EmptyFilters,
      isCompleted: selectedTab === TaskState.COMPLETED
    }),
    [selectedTab]
  );

  const allTasksFilters = useMemo(
    () => ({
      ...baseFiltersForTotals,
      $or: [{ assigneeId: userData?.id }, { createdBy: userData?.id }]
    }),
    [baseFiltersForTotals, userData?.id]
  );

  const assignedToMeFilters = useMemo(
    () => ({
      ...baseFiltersForTotals,
      assigneeId: userData?.id,
      createdBy: undefined
    }),
    [baseFiltersForTotals, userData?.id]
  );

  const assignedByMeFilters = useMemo(
    () => ({
      ...baseFiltersForTotals,
      assigneeId: { ne: userData?.id },
      createdBy: userData?.id
    }),
    [baseFiltersForTotals, userData?.id]
  );

  const isCurrentUserResolved = Boolean(userData?.id);

  const allAssignedToMeFilters = useMemo(
    () =>
      isCurrentUserResolved
        ? {
            ...EmptyFilters,
            assigneeId: userData?.id,
            createdBy: undefined
          }
        : EmptyFilters,
    [isCurrentUserResolved, userData?.id]
  );

  const allAssignedByMeFilters = useMemo(
    () =>
      isCurrentUserResolved
        ? {
            ...EmptyFilters,
            assigneeId: { ne: userData?.id },
            createdBy: userData?.id
          }
        : EmptyFilters,
    [isCurrentUserResolved, userData?.id]
  );

  const handleUndoDeleteTask = (taskId: string) => {
    undoDeleteTask.mutate(
      {
        pathParams: {
          taskId
        }
      },
      {
        onSuccess: () => {
          refreshTasks();
        }
      }
    );
  };

  const handleDeleteTask = () => {
    if (!deleteTaskModalState?.task?.id) {
      setIsDeleteTaskModalOpen(false);
      return;
    }
    const taskId = deleteTaskModalState?.task?.id;
    deleteTask.mutate(
      {
        pathParams: {
          taskId
        }
      },
      {
        onSuccess: () => {
          setDeleteTaskModalState({
            isOpen: false,
            task: { id: null, name: null }
          });
          removeTaskFromLists(taskId);
          toast.success(
            <SuccessToast
              undoLabel={t("undo")}
              successTitle={t(`${TRANSLATION_KEY}.taskDeleted`)}
              handleUndo={() => handleUndoDeleteTask(taskId)}
              refreshTasks={refreshTasks}
            />,
            {
              duration: 3000
            }
          );
        }
      }
    );
  };

  const { total: allTasksTotal = 0 } = useFilteredTasks(
    allTasksFilters,
    searchTerm ? { term: searchTerm } : undefined
  );

  const { total: assignedToMeTotal = 0 } = useFilteredTasks(
    assignedToMeFilters,
    searchTerm ? { term: searchTerm } : undefined
  );

  const { total: assignedByMeTotal = 0 } = useFilteredTasks(
    assignedByMeFilters,
    searchTerm ? { term: searchTerm } : undefined
  );

  const { total: allAssignedToMeTotal = 0, isLoading: allAssignedToMeLoading } =
    useFilteredTasks(
      allAssignedToMeFilters,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      isCurrentUserResolved
    );

  const { total: allAssignedByMeTotal = 0, isLoading: allAssignedByMeLoading } =
    useFilteredTasks(
      allAssignedByMeFilters,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      isCurrentUserResolved
    );

  const refreshTasks = useRefreshFilteredTasks();
  const removeTaskFromLists = useRemoveTaskFromLists();

  const setIsTaskModalOpen = createModalStateSetter(setTaskModalState);
  const setIsDeleteTaskModalOpen = createModalStateSetter(
    setDeleteTaskModalState
  );

  const enhancedLayoutTabsProps: tabsPropsType = useMemo(
    () => ({
      ...layoutTabsProps,
      options: Object.entries(layoutTabsProps.options).reduce(
        (acc, [key, value]) => {
          let total = 0;
          if (key === TaskTabs.ALL_TASKS) {
            total = allTasksTotal;
          } else if (key === TaskTabs.ASSIGNED_TO_ME) {
            total = assignedToMeTotal;
          } else {
            total = assignedByMeTotal;
          }
          acc[key] = (
            <div className="flex items-center gap-[6px]">
              <span>{value}</span>
              {total > 0 && <CounterBadge count={total} />}
            </div>
          );
          return acc;
        },
        {} as {
          [key: string]: string | JSX.Element;
        }
      )
    }),
    [layoutTabsProps, allTasksTotal, assignedToMeTotal, assignedByMeTotal]
  );

  const shouldShowEmptyState = useMemo(
    () =>
      isCurrentUserResolved &&
      !allAssignedToMeLoading &&
      !allAssignedByMeLoading &&
      allAssignedToMeTotal + allAssignedByMeTotal === 0,
    [
      isCurrentUserResolved,
      allAssignedToMeLoading,
      allAssignedByMeLoading,
      allAssignedToMeTotal,
      allAssignedByMeTotal
    ]
  );

  return (
    <>
      <WidgetCard
        cardHeight="35rem"
        title={t(`${TRANSLATION_KEY}.title`)}
        contentClassName="p-0 pt-3"
        tabs={
          !shouldShowEmptyState ? (
            <Tabs
              {...tabsProps}
              valueSelected={(value) => setSelectedTab(value)}
            />
          ) : undefined
        }
        showFeedBackButton={false}
        customOptions={
          <div className="flex gap-1 items-center">
            <SearchButtonInput
              placeholder={t("search")}
              inputValue={searchTerm}
              setInputValue={setSearchTerm}
              searchIconColor="secondary"
              size="small"
              tooltipText={t(`${TRANSLATION_KEY}.search`)}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  data-test="add-task"
                  variant="iconNoBg"
                  iconColor="secondary"
                  onClick={() =>
                    setTaskModalState({
                      editTaskId: null,
                      isEditMode: false,
                      isOpen: true
                    })
                  }
                >
                  <Add />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>{t(`${TRANSLATION_KEY}.addTask`)}</TooltipContent>
            </Tooltip>
          </div>
        }
      >
        {shouldShowEmptyState ? (
          <div className="flex w-full h-full justify-center items-center">
            <EmptyTasksState />
          </div>
        ) : (
          <div className="flex flex-col w-full">
            <div>
              <LayoutTabs
                {...enhancedLayoutTabsProps}
                valueSelected={(newTabValue) => {
                  setSelectedLayoutTab(newTabValue);
                }}
              />
            </div>
            <TasksTable
              selectedLayoutTab={selectedLayoutTab as string}
              selectedTab={selectedTab as string}
              allTasksFilters={allTasksFilters}
              assignedToMeFilters={assignedToMeFilters}
              assignedByMeFilters={assignedByMeFilters}
              searchTerm={searchTerm}
              userId={userData?.id}
              setTaskModalState={setTaskModalState}
              setDeleteTaskModalState={setDeleteTaskModalState}
              columnCustomizationEnabled={false}
            />
          </div>
        )}
      </WidgetCard>
      <TaskModal
        isOpen={taskModalState.isOpen}
        setIsOpen={setIsTaskModalOpen}
        isEditMode={taskModalState.isEditMode}
        editTaskId={taskModalState.editTaskId}
        editTaskData={taskModalState.editTaskData}
      />
      <ConfirmationModal
        isOpen={deleteTaskModalState.isOpen}
        setIsOpen={setIsDeleteTaskModalOpen}
        title={t(`${TRANSLATION_KEY}.deleteConfirmation.title`)}
        confirmationText={t(
          `${TRANSLATION_KEY}.deleteConfirmation.confirmation`,
          { taskName: deleteTaskModalState.task.name }
        )}
        handleSubmit={handleDeleteTask}
      />
    </>
  );
};

export default TasksFocus;
